import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Check, Circle, ChevronRight } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

// =====================================================================
// Settings → Setup Guide
//
// Read-only activation checklist. Lists the things a new clinic should
// do to get going (letterhead, hours, chairs, services, team, methods).
// Each item shows whether it's done, a one-line description, and a
// "Go" link to the page that lets the user act on it.
//
// Backed by dos_v2.get_setup_progress() — it just reads existing state
// (counts, jsonb-emptiness checks) and returns done/not-done.
// =====================================================================

export default function SettingsSetupGuide() {
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [items, setItems]     = useState([]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const { data, error: e } = await supabase.rpc("get_setup_progress");
    setLoading(false);
    if (e) { setError(e.message ?? "Could not load."); return; }
    setItems(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const done = items.filter((i) => i.done).length;
  const total = items.length;
  const pct  = total > 0 ? Math.round((done / total) * 100) : 0;

  if (loading) {
    return <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-sm text-slate-400">
      <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…
    </div>;
  }
  if (error) {
    return <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>;
  }

  return (
    <div className="space-y-4" data-testid="settings-setup">
      {/* Progress header */}
      <div className="bg-white rounded-2xl shadow-sm px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Setup guide</h1>
            <p className="text-xs text-slate-500">Quick checklist of the things every clinic should set up before going live.</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-semibold text-slate-900">{done}/{total}</p>
            <p className="text-xs text-slate-500">complete</p>
          </div>
        </div>
        <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-teal-500 transition-all duration-300" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Items */}
      <ul className="bg-white rounded-2xl shadow-sm divide-y divide-slate-100" data-testid="setup-list">
        {items.map((it) => (
          <li key={it.key}>
            <Link to={it.route} data-testid={`setup-item-${it.key}`}
              className={`flex items-center gap-4 px-5 py-3 hover:bg-slate-50 ${it.done ? "" : ""}`}>
              {it.done ? (
                <Check className="w-5 h-5 text-emerald-500 shrink-0" strokeWidth={2.5} />
              ) : (
                <Circle className="w-5 h-5 text-slate-300 shrink-0" strokeWidth={2} />
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${it.done ? "text-slate-500 line-through" : "text-slate-800"}`}>
                  {it.label}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">{it.desc}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
            </Link>
          </li>
        ))}
      </ul>

      {done === total && total > 0 ? (
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-5 py-4 text-sm text-emerald-800">
          🎉 You're all set. Time to start booking.
        </div>
      ) : null}
    </div>
  );
}
